<template>
	<div class="form-area">
		<el-scrollbar class="form-scroll">
			<div v-loading="!formLoaded" class="form-container">
				<slot name="tip"></slot>

				<el-form
					v-if="formLoaded"
					ref="formRef"
					:model="formModel"
					:rules="formRules"
					label-position="left"
				>
					<template v-for="item in personalFields">
						<!-- 留言内容 -->
						<el-form-item
							v-if="item.fieldType === 0 && Number(item.systemType) === 7"
							:key="item.uuid"
							:prop="getFieldProp(item)"
						>
							<template #label>
								<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
							</template>
							<el-input
								type="textarea"
								:value="getFieldValue(item)"
								:placeholder="item.dynamicFieldDescribed"
								maxlength="1000"
								show-word-limit
								rows="5"
								@input="val => handleSystemInput(item, val)"
							></el-input>
						</el-form-item>
						<!-- 姓名 -->
						<el-form-item
							v-if="item.fieldType === 0 && Number(item.systemType) === 1"
							:key="item.uuid"
							:prop="getFieldProp(item)"
						>
							<template #label>
								<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
							</template>
							<el-input
								:value="getFieldValue(item)"
								:placeholder="item.dynamicFieldDescribed"
								maxlength="200"
								@input="val => handleSystemInput(item, val)"
							></el-input>
						</el-form-item>
						<!-- 联系方式 -->
						<el-form-item
							v-if="item.fieldType === 0 && Number(item.systemType) === 2"
							:key="item.uuid"
							:prop="getFieldProp(item)"
						>
							<template #label>
								<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
							</template>
							<c-phone-item
								:item="contactModel"
								:maxlength="100"
								:required="item.config === 1"
								:placeholder="item.dynamicFieldDescribed"
								@change="handleAreaCodeChange"
							></c-phone-item>
						</el-form-item>
						<!-- 机构 -->
						<el-form-item
							v-if="item.fieldType === 0 && Number(item.systemType) === 3"
							:key="item.uuid"
							:prop="getFieldProp(item)"
						>
							<template #label>
								<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
							</template>
							<el-autocomplete
								style="width: 100%"
								:value="getFieldValue(item)"
								:fetch-suggestions="queryInstitutionList"
								popper-class="company-autocomplete-popper"
								:placeholder="item.dynamicFieldDescribed"
								:maxlength="200"
								@input="val => handleCompanyInput(item, val)"
								@select="option => handleCompanySelect(item, option)"
							>
								<template slot-scope="{ item: option }">
									<div class="company-option-name">{{ option.fullname }}</div>
								</template>
							</el-autocomplete>
						</el-form-item>
						<!-- 职位 -->
						<el-form-item
							v-if="item.fieldType === 0 && Number(item.systemType) === 4"
							:key="item.uuid"
							:prop="getFieldProp(item)"
						>
							<template #label>
								<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
							</template>
							<el-input
								:value="getFieldValue(item)"
								:placeholder="item.dynamicFieldDescribed"
								@input="val => handleSystemInput(item, val)"
							></el-input>
						</el-form-item>
						<!-- 邮箱 -->
						<el-form-item
							v-if="item.fieldType === 0 && Number(item.systemType) === 5"
							:key="item.uuid"
							:prop="getFieldProp(item)"
						>
							<template #label>
								<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
							</template>
							<el-input
								:value="getFieldValue(item)"
								:placeholder="item.dynamicFieldDescribed"
								maxlength="50"
								@input="val => handleSystemInput(item, val)"
							></el-input>
						</el-form-item>
						<!-- 邮箱 & 验证码 -->
						<template v-if="item.fieldType === 0 && Number(item.systemType) === 8">
							<el-form-item
								:key="`${item.uuid}-email`"
								:prop="getFieldProp(item)"
							>
								<template #label>
									<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
								</template>
								<el-input
									:value="getFieldValue(item)"
									:placeholder="item.dynamicFieldDescribed"
									maxlength="128"
									@input="val => handleSystemInput(item, val)"
								></el-input>
							</el-form-item>
							<el-form-item
								:key="`${item.uuid}-auth`"
								prop="authCode"
							>
								<template #label>
									<ellipsis-two-line-tooltip :text="proxy.$t('enterprise.normal-field.verification')" />
								</template>
								<div class="auth-code-wrapper">
									<el-input
										:value="innerVerification"
										:placeholder="verificationPlaceholder"
										@input="handleVerificationInput"
									>
										<div
											slot="suffix"
											class="auth-code-suffix"
											:class="{ 'auth-code-suffix--disabled': countdown > 0 }"
											@click="handleSendVerifyCode(item)"
										>
											<span v-if="countdown === 0">{{
												proxy.$t('enterprise.normal-field.send-code')
											}}</span>
											<span v-else class="auth-code-suffix-disabled">{{ countdown }}S</span>
										</div>
									</el-input>
								</div>
							</el-form-item>
						</template>
						<!-- 名片 -->
						<template v-if="item.fieldType === 0 && Number(item.systemType) === 6">
							<!-- 不做展示 -->
						</template>
						<!-- 自定义字段 -->
						<el-form-item
							v-if="item.fieldType === 1"
							:key="item.uuid"
							:prop="getFieldProp(item)"
						>
							<template #label>
								<ellipsis-two-line-tooltip :text="item.dynamicFieldName" />
							</template>
							<component
								:is="getCustomFieldComponent(item.type)"
								:field="item"
								@update="val => handleFieldUpdate(item, val)"
							/>
						</el-form-item>
					</template>
				</el-form>

				<slot name="append"></slot>
			</div>
		</el-scrollbar>
	</div>
</template>

<script setup>
import { ref, watch, getCurrentInstance, computed, onUnmounted } from 'vue';
import { getCustomFieldComponent } from '@rebuild/components/custom-fields';
import { SYSTEM_FIELD_MAP } from '@rebuild/components/custom-fields/systemFieldMap';
import { useInstitutionSuggestions } from '@rebuild/components/custom-fields/useInstitutionSuggestions';
import EllipsisTwoLineTooltip from '@rebuild/components/custom-fields/EllipsisTwoLineTooltip.vue';
import CPhoneItem from '@/components/c-phone/c-phone-item.vue';

const { proxy } = getCurrentInstance();

const props = defineProps({
	personalFields: {
		type: Array,
		default: () => []
	},
	sendVerifyCode: {
		type: Function,
		default: null
	}
});
const emits = defineEmits(['update', 'update-area-code', 'update-verification']);

const formRef = ref(null);
const formLoaded = ref(false);
const formModel = ref({});
const formRules = ref({});
const contactModel = ref({
	areaCode: '+86',
	phone: ''
});
const contactField = ref(null);

const normalizeNumber = val => val.replace(/\D/g, '');
const validateEmail = (rule, value, callback) => {
	if (!value) {
		rule.required ? callback(new Error(proxy.$t('enterprise.field.tips', { label: 'email' }))) : callback();
	} else if (!/^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/.test(value)) {
		callback(new Error(proxy.$t('enterprise.field.success-email')));
	} else {
		callback();
	}
};
const getFieldValidTips = label => proxy.$t(`enterprise.field.tips`, { label });
const verificationPlaceholder = computed(() =>
	proxy.$t('enterprise.field.tips', { label: proxy.$t('enterprise.normal-field.verification') })
);

const getFieldProp = field => {
	if (field.fieldType === 0) return SYSTEM_FIELD_MAP[String(field.systemType)];
	return field.uuid;
};

const getFieldValue = field => {
	const prop = getFieldProp(field);
	if (!prop) return '';
	return formModel.value[prop] ?? field.dynamicFieldValue ?? '';
};

const emitUpdate = valid => {
	emits('update', {
		formData: formModel.value,
		personalFields: props.personalFields,
		valid
	});
};

const buildFormState = () => {
	formRules.value = {};
	formModel.value = {};
	contactField.value = null;
	clearVerifyCountdown();
	innerVerification.value = '';
	proxy.$set(formModel.value, 'authCode', '');
	emits('update-verification', '');

	proxy.$set(contactModel.value, 'areaCode', '+86');
	proxy.$set(contactModel.value, 'phone', '');

	const list = props.personalFields || [];
	if (!list.length) {
		formLoaded.value = true;
		return;
	}

	list.forEach(item => {
		const prop = getFieldProp(item);
		if (!prop) return;
		proxy.$set(formModel.value, prop, item.dynamicFieldValue || '');
		proxy.$set(formRules.value, prop, [
			{
				required: item.config === 1,
				message: item.dynamicFieldDescribed || getFieldValidTips(item.dynamicFieldName),
				trigger: 'blur'
			}
		]);
		if (item.fieldType === 0 && [5, 8].includes(Number(item.systemType))) {
			formRules.value[prop].push({ validator: validateEmail, trigger: 'blur' });
		}
		if (item.fieldType === 0 && Number(item.systemType) === 8) {
			proxy.$set(formRules.value, 'authCode', [
				{
					required: item.config === 1,
					message: getFieldValidTips(proxy.$t('enterprise.normal-field.verification')),
					trigger: 'blur'
				}
			]);
		}
		if (item.fieldType === 0 && Number(item.systemType) === 2) {
			contactField.value = item;
			proxy.$set(contactModel.value, 'phone', item.dynamicFieldValue || '');
			proxy.$set(contactModel.value, 'areaCode', item.areaCode || '+86');
			emits('update-area-code', contactModel.value.areaCode);
		}
	});
	formLoaded.value = true;
	emitUpdate();
};
const onceTag = ref(false);
watch(
	() => props.personalFields,
	() => {
		if (onceTag.value) return;
		onceTag.value = true;
		buildFormState();
	}
);

const handleSystemInput = (field, value) => {
	const prop = getFieldProp(field);
	if (!prop) return;
	let nextValue = value;
	if (Number(field.systemType) === 2) nextValue = normalizeNumber(value);
	proxy.$set(field, 'dynamicFieldValue', nextValue);
	proxy.$set(formModel.value, prop, nextValue);
	emitUpdate();
};

const syncContactField = () => {
	if (!contactField.value) return;
	const prop = getFieldProp(contactField.value);
	const phone = contactModel.value.phone || '';
	const areaCode = contactModel.value.areaCode || '+86';
	proxy.$set(contactField.value, 'dynamicFieldValue', phone);
	proxy.$set(contactField.value, 'areaCode', areaCode);
	if (prop) proxy.$set(formModel.value, prop, phone);
	emits('update-area-code', areaCode);
	emitUpdate();
};

const handleAreaCodeChange = payload => {
	const areaCode = payload?.areaCode;
	proxy.$set(contactModel.value, 'areaCode', areaCode);
	syncContactField();
};

// 机构
const { queryInstitutionList } = useInstitutionSuggestions();
const handleCompanyInput = (field, value) => {
	handleSystemInput(field, value || '');
};
const handleCompanySelect = (field, option) => {
	handleSystemInput(field, option?.fullname || '');
};

const handleFieldUpdate = (field, value) => {
	if (!field) return;
	const prop = getFieldProp(field);
	proxy.$set(field, 'dynamicFieldValue', value);
	if (prop) proxy.$set(formModel.value, prop, value);
	emitUpdate();
};

// 验证码
const innerVerification = ref('');
const countdown = ref(0);
let verifyCodeTimer = null;
const clearVerifyCountdown = () => {
	if (verifyCodeTimer) {
		clearInterval(verifyCodeTimer);
		verifyCodeTimer = null;
	}
	countdown.value = 0;
};
const handleVerificationInput = value => {
	const nextValue = value || '';
	innerVerification.value = nextValue;
	proxy.$set(formModel.value, 'authCode', nextValue);
	emits('update-verification', nextValue);
	emitUpdate();
};
const handleSendVerifyCode = async field => {
	if (countdown.value > 0) return;
	const beforeHook = props.sendVerifyCode;
	if (typeof beforeHook !== 'function') return;

	try {
		await beforeHook({ field, email: field?.dynamicFieldValue || '' });
		countdown.value = 60;
		verifyCodeTimer = setInterval(() => {
			if (countdown.value > 0) {
				countdown.value--;
			} else {
				clearVerifyCountdown();
			}
		}, 1000);
	} catch (_) {
		// 错误由使用者处理
	}
};
onUnmounted(() => {
	clearVerifyCountdown();
});

const validate = () => {
	return new Promise(resolve => {
		syncContactField();
		if (!formRef.value) {
			resolve(true);
			return;
		}
		formRef.value.validate(valid => {
			emitUpdate(valid);
			resolve(valid);
		});
	});
};
const resetFields = () => {
	formRef.value && formRef.value.resetFields();
	innerVerification.value = '';
	proxy.$set(formModel.value, 'authCode', '');
	emits('update-verification', '');
	clearVerifyCountdown();
};
const clearValidate = () => {
	formRef.value && formRef.value.clearValidate();
};

defineExpose({
	validate,
	resetFields,
	clearValidate
});
</script>

<style lang="less" scoped>
.form-area {
	height: 56vh;
	display: flex;
	flex-direction: column;

	.form-scroll {
		flex: 1;

		/deep/ .el-scrollbar__wrap {
			padding-right: 32px;
			overflow-x: hidden;
		}
	}
	.el-form {
		position: relative; // 校验符号定位
	}
}
.company-autocomplete-popper .company-option-name {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.auth-code-wrapper {
	/deep/ .el-input__inner {
		padding-right: 100px !important;
	}
	.auth-code-suffix {
		padding-right: 5px;
		color: #3964e5;
		font-size: 14px;
		font-weight: 400;
		line-height: 18px;
		cursor: pointer;
		&--disabled {
			cursor: not-allowed;
		}
	}
	.auth-code-suffix-disabled {
		color: rgba(57, 100, 229, 0.6);
	}
}
</style>
